import React, { useEffect, useRef, useState } from "react";
import { makeDivResizable, makeDraggable } from "shared/utils/draggable";
import "./VideoTrackPlayer.css";

import {
  AudioOutlined,
  AudioMutedOutlined,
  SoundOutlined,
  SettingOutlined,
  StopOutlined,
  CameraOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { Button, Tooltip, Modal, Form, Select } from "antd";
import logger from "shared/utils/logging";

const VideoTrackPlayer = (props) => {
  const videoMediaStream = props.videoMediaStream;
  const audioMediaStream = props.audioMediaStream;
  const onVideoToggleClicked = props.onVideoToggleClicked || ((enabled) => {});
  const onAudioToggleClicked = props.onAudioToggleClicked || ((enabled) => {});
  const onLocalMicToggleClicked =
    props.onLocalMicToggleClicked || ((enabled) => {});

  const allowLocalAudio = props?.allowLocalAudio
    ? props.allowLocalAudio
    : false;
  const allowRemoteAudio = props?.allowRemoteAudio
    ? props.allowRemoteAudio
    : false;
  const allowRemoteVideo = props?.allowRemoteVideo
    ? props.allowRemoteVideo
    : false;

  const mediaAllowed = props?.mediaAllowed ? props.mediaAllowed : false;
  const mediaError = props?.mediaError ? props.mediaError : null;

  const isSendingAudio = props?.isSendingAudio ? props.isSendingAudio : false;
  const isReceiveAudio = props?.isReceiveAudio ? props.isReceiveAudio : false;
  const isReceiveVideo = props?.isReceiveVideo ? props.isReceiveVideo : false;

  const [audioMuted, setAudioMuted] = useState(true);
  const [audioDisabled, setAudioDisabled] = useState(false);

  const [videoStopped, setVideoStopped] = useState(false);
  const [videoDisabled, setVideoDisabled] = useState(false);

  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [form] = Form.useForm();

  const videoTag = useRef(null);
  const audioTag = useRef(null);

  const waitingToPlayAudioRef = useRef(false);
  const userInteractionCleanupRef = useRef(null);

  const waitingUserInteractionToPlayAudio = () => {
    logger.debug("Waiting user interaction to unmute the audio");

    // Clean up any existing event listeners first
    if (userInteractionCleanupRef.current) {
      userInteractionCleanupRef.current();
      userInteractionCleanupRef.current = null;
    }

    const handleUserInteraction = async () => {
      if (waitingToPlayAudioRef.current && audioTag.current) {
        try {
          await audioTag.current.play();
          waitingToPlayAudioRef.current = false;
          logger.debug("Audio playback succeeded after user interaction");
        } catch (error) {
          logger.error("Audio playback failed again:", error);
        }
      }
      // Clean up event listeners
      if (userInteractionCleanupRef.current) {
        userInteractionCleanupRef.current();
        userInteractionCleanupRef.current = null;
      }
    };

    // Add event listeners for user interaction
    window.addEventListener("click", handleUserInteraction);
    window.addEventListener("keydown", handleUserInteraction);
    window.addEventListener("touchstart", handleUserInteraction);

    // Store cleanup function
    userInteractionCleanupRef.current = () => {
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
      window.removeEventListener("touchstart", handleUserInteraction);
    };
  };

  useEffect(() => {
    logger.log("VideoTrackViewer set video track");
    const remoteVideo = videoTag.current;

    if (!remoteVideo) return;

    // Clean up previous stream
    if (remoteVideo.srcObject && remoteVideo.srcObject !== videoMediaStream) {
      logger.log("Cleaning up previous video stream");
      const previousStream = remoteVideo.srcObject;

      // Pause and clear the video element first
      remoteVideo.pause();
      remoteVideo.srcObject = null;

      // Remove event listeners to prevent memory leaks
      remoteVideo.onloadedmetadata = null;
      remoteVideo.oncanplay = null;
      remoteVideo.onerror = null;

      // Stop all tracks in the previous stream
      if (previousStream && previousStream.getTracks) {
        previousStream.getTracks().forEach((track) => {
          if (track.readyState === "live") {
            track.stop();
          }
        });
      }
    }

    if (videoMediaStream == null) {
      setVideoDisabled(true);
      if (remoteVideo.srcObject) {
        remoteVideo.pause();
        remoteVideo.srcObject = null;
      }
    } else {
      setVideoDisabled(false);
      setVideoStopped(false);
      // Set new stream with proper error handling
      logger.log("VideoTrackViewer set remoteVideo.srcObject");
      remoteVideo.srcObject = videoMediaStream;

      // Force reload and play
      remoteVideo.load();
      remoteVideo.play().catch((error) => {
        logger.error("Error playing video:", error);
      });
    }

    // Cleanup function for when component unmounts or stream changes
    return () => {
      if (remoteVideo.srcObject && remoteVideo.srcObject === videoMediaStream) {
        remoteVideo.pause();
        remoteVideo.srcObject = null;
        remoteVideo.onloadedmetadata = null;
        remoteVideo.oncanplay = null;
        remoteVideo.onerror = null;
      }
    };
  }, [videoMediaStream]);

  useEffect(() => {
    logger.log("VideoTrackViewer set audio track: ", audioMediaStream);
    const remoteAudio = audioTag.current;

    if (!remoteAudio) return;

    // Clean up previous audio stream
    if (remoteAudio.srcObject && remoteAudio.srcObject !== audioMediaStream) {
      logger.log("Cleaning up previous audio stream");
      const previousStream = remoteAudio.srcObject;

      // Pause and clear the audio element first
      remoteAudio.pause();
      remoteAudio.srcObject = null;

      // Remove event listeners to prevent memory leaks
      remoteAudio.onloadedmetadata = null;
      remoteAudio.oncanplay = null;
      remoteAudio.onerror = null;

      // Stop all tracks in the previous stream
      if (previousStream && previousStream.getTracks) {
        previousStream.getTracks().forEach((track) => {
          if (track.readyState === "live") {
            track.stop();
          }
        });
      }
    }

    if (audioMediaStream == null) {
      setAudioDisabled(true);
      if (remoteAudio.srcObject) {
        remoteAudio.pause();
        remoteAudio.srcObject = null;
      }
    } else {
      setAudioDisabled(false);

      logger.debug("audio track set remoteAudio.srcObject");
      remoteAudio.srcObject = audioMediaStream;
      logger.log("play audio: ", remoteAudio.srcObject);
      remoteAudio
        .play()
        .then(() => {
          waitingToPlayAudioRef.current = false;
          setAudioMuted(false);
          logger.debug("audio played");
        })
        .catch((error) => {
          logger.log("Audio playback prevented:", error);
          waitingToPlayAudioRef.current = true;
          setAudioMuted(true);
          waitingUserInteractionToPlayAudio();
        });
    }

    // Cleanup function for when component unmounts or stream changes
    return () => {
      if (remoteAudio.srcObject && remoteAudio.srcObject === audioMediaStream) {
        remoteAudio.pause();
        remoteAudio.srcObject = null;
        remoteAudio.onloadedmetadata = null;
        remoteAudio.oncanplay = null;
        remoteAudio.onerror = null;
      }
    };
  }, [audioMediaStream]);

  const divCameraViewerContainer = useRef(null);
  useEffect(() => {
    makeDivResizable(divCameraViewerContainer.current);
  }, []);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      logger.log("VideoTrackPlayer cleanup on unmount");

      // Clean up user interaction event listeners
      if (userInteractionCleanupRef.current) {
        userInteractionCleanupRef.current();
        userInteractionCleanupRef.current = null;
      }

      // Clean up video element
      if (videoTag.current) {
        const video = videoTag.current;
        video.pause();
        if (video.srcObject) {
          const stream = video.srcObject;
          video.srcObject = null;
          // Stop all tracks
          if (stream && stream.getTracks) {
            stream.getTracks().forEach((track) => {
              if (track.readyState === "live") {
                track.stop();
              }
            });
          }
        }
        video.onloadedmetadata = null;
        video.oncanplay = null;
        video.onerror = null;
      }

      // Clean up audio element
      if (audioTag.current) {
        const audio = audioTag.current;
        audio.pause();
        if (audio.srcObject) {
          const stream = audio.srcObject;
          audio.srcObject = null;
          // Stop all tracks
          if (stream && stream.getTracks) {
            stream.getTracks().forEach((track) => {
              if (track.readyState === "live") {
                track.stop();
              }
            });
          }
        }
        audio.onloadedmetadata = null;
        audio.oncanplay = null;
        audio.onerror = null;
      }

      waitingToPlayAudioRef.current = false;
    };
  }, []);

  const onStopCameraButtonClicked = () => {
    logger.log("stop camera: ", !videoStopped);
  };

  const onMuteAudioButtonClicked = () => {
    logger.log("Mute: ", !audioMuted);
    const remoteAudio = audioTag.current;
    if (audioMuted)
      remoteAudio
        .play()
        .then(() => {
          setAudioMuted(false);
          logger.debug("audio played");
        })
        .catch((error) => {
          logger.log("Audio playback prevented:", error);
        });
    else {
      remoteAudio.pause();
      setAudioMuted(true);
    }
  };

  const onBtnConfigCameraClicked = () => {
    // Get current resolution and FPS, then update form values
    const currentResolution = getCurrentVideoResolution();
    const currentFPS = getCurrentVideoFPS();

    if (currentResolution) {
      const resolutionString = `${currentResolution.width}x${currentResolution.height}`;
      const fpsValue = currentFPS ? currentFPS.fps : 30;

      form.setFieldsValue({
        resolution: resolutionString,
        fps: fpsValue,
      });

      logger.log("Current resolution:", resolutionString);
      logger.log("Current FPS:", fpsValue);
    }

    setConfigModalVisible(true);
  };

  // Function to get current video resolution
  const getCurrentVideoResolution = () => {
    if (!videoMediaStream) return null;

    const videoTrack = videoMediaStream.getVideoTracks()[0];
    if (!videoTrack) return null;

    // Method 3: Get from track constraints (fallback)
    const constraints = videoTrack.getConstraints();
    if (constraints.width && constraints.height) {
      return {
        width: constraints.width,
        height: constraints.height,
        method: "constraints",
      };
    }

    // Method 1: Get from track settings (most reliable)
    const settings = videoTrack.getSettings();
    if (settings.width && settings.height) {
      return {
        width: settings.width,
        height: settings.height,
        method: "settings",
      };
    }

    // Method 2: Get from video element (if available)
    if (videoTag.current) {
      const video = videoTag.current;
      if (video.videoWidth && video.videoHeight) {
        return {
          width: video.videoWidth,
          height: video.videoHeight,
          method: "videoElement",
        };
      }
    }

    return null;
  };

  // Function to get current video FPS
  const getCurrentVideoFPS = () => {
    if (!videoMediaStream) return null;

    const videoTrack = videoMediaStream.getVideoTracks()[0];
    if (!videoTrack) return null;

    // Method 2: Get from track constraints (fallback)
    const constraints = videoTrack.getConstraints();
    if (constraints.frameRate) {
      // Handle different constraint formats
      if (constraints.frameRate.max) {
        logger.log(
          "Current FPS from constraints max:",
          constraints.frameRate.max
        );
        return {
          fps: Math.round(constraints.frameRate.max), // Round to nearest integer
          method: "constraints",
        };
      } else if (typeof constraints.frameRate === "number") {
        logger.log(
          "Current FPS from constraints frameRate:",
          constraints.frameRate
        );
        return {
          fps: Math.round(constraints.frameRate), // Round to nearest integer
          method: "constraints",
        };
      } else if (constraints.frameRate.ideal) {
        logger.log(
          "Current FPS from constraints ideal:",
          constraints.frameRate.ideal
        );
        return {
          fps: Math.round(constraints.frameRate.ideal), // Round to nearest integer
          method: "constraints",
        };
      }

      // Method 1: Get from track settings (most reliable)
      const settings = videoTrack.getSettings();
      if (settings.frameRate) {
        logger.log("Current FPS from settings:", settings.frameRate);
        return {
          fps: Math.round(settings.frameRate), // Round to nearest integer
          method: "settings",
        };
      }
    }

    return null;
  };

  const resolutionOptions = [
    { label: "320x240", value: "320x240" },
    { label: "640x480", value: "640x480" },
    { label: "1280x720", value: "1280x720" },
    { label: "1920x1080", value: "1920x1080" },
  ];

  // Add current resolution to options if it's not already there
  const getCurrentResolutionString = () => {
    const currentResolution = getCurrentVideoResolution();
    if (currentResolution) {
      return `${currentResolution.width}x${currentResolution.height}`;
    }
    return "640x480"; // default fallback
  };

  const getResolutionOptions = () => {
    const currentResString = getCurrentResolutionString();
    const exists = resolutionOptions.some(
      (option) => option.value === currentResString
    );

    if (!exists && getCurrentVideoResolution()) {
      return [
        ...resolutionOptions,
        {
          label: `${currentResString} (Current)`,
          value: currentResString,
        },
      ];
    }

    return resolutionOptions.map((option) => {
      if (option.value === currentResString) {
        return {
          ...option,
          label: `${option.label} (Current)`,
        };
      }
      return option;
    });
  };

  const fpsOptions = [
    { label: "15 FPS", value: 15 },
    { label: "30 FPS", value: 30 },
    { label: "60 FPS", value: 60 },
  ];

  // Function to get current FPS and add to options if needed
  const getCurrentFPSValue = () => {
    const currentFPS = getCurrentVideoFPS();
    return currentFPS ? currentFPS.fps : 30; // default fallback
  };

  const getFPSOptions = () => {
    const currentFPSValue = getCurrentFPSValue();
    const exists = fpsOptions.some(
      (option) => option.value === currentFPSValue
    );

    if (!exists && getCurrentVideoFPS()) {
      return [
        ...fpsOptions,
        {
          label: `${currentFPSValue} FPS (Current)`,
          value: currentFPSValue,
        },
      ];
    }

    return fpsOptions.map((option) => {
      if (option.value === currentFPSValue) {
        return {
          ...option,
          label: `${option.label} (Current)`,
        };
      }
      return option;
    });
  };

  const handleConfigSubmit = (values) => {
    logger.log("Camera config:", values);

    // Parse resolution string to width/height object
    if (values.resolution) {
      const [width, height] = values.resolution.split("x").map(Number);
      const configData = {
        resolution: { width, height },
        fps: values.fps,
      };
      logger.log("Parsed config:", configData);
      if (props.onCameraConfigChanged) {
        props.onCameraConfigChanged(configData);
      }
    }

    setConfigModalVisible(false);
  };

  return (
    <div className="video-player" ref={divCameraViewerContainer}>
      <div className="media-control">
        {allowLocalAudio && (
          <Tooltip title={mediaError ? mediaError : "Mute/Unmute local mic"}>
            <Button
              disabled={!mediaAllowed}
              id="btnLocalMic"
              icon={isSendingAudio ? <AudioOutlined /> : <AudioMutedOutlined />}
              type={isSendingAudio ? "primary" : "default"}
              onClick={onLocalMicToggleClicked}
            ></Button>
          </Tooltip>
        )}
        {allowRemoteVideo && (
          <Tooltip title={"On/Off remote camera"}>
            <Button
              id="btnCamera"
              type={isReceiveVideo ? "primary" : "default"}
              disabled={videoDisabled}
              icon={
                isReceiveVideo ? <CameraOutlined /> : <EyeInvisibleOutlined />
              }
              onClick={onVideoToggleClicked}
            />
          </Tooltip>
        )}

        {allowRemoteAudio && (
          <Tooltip title={"Mute/Unmute remote mic"}>
            <Button
              id="btnSoundMute"
              // icon={audioMuted ? <StopOutlined /> : <SoundOutlined />}
              icon={<SoundOutlined />}
              type={isReceiveAudio ? "primary" : "default"}
              disabled={audioDisabled}
              onClick={onAudioToggleClicked}
            />
          </Tooltip>
        )}
        {allowRemoteVideo && (
          <Tooltip title={"Config remote camera"}>
            <Button
              id="btnConfigCamera"
              disabled={videoDisabled}
              icon={<SettingOutlined />}
              onClick={onBtnConfigCameraClicked}
            />
          </Tooltip>
        )}
      </div>
      <audio
        id="audio"
        autoPlay={true}
        ref={audioTag}
        playsInline={true}
      ></audio>

      <video
        id="video"
        ref={videoTag}
        autoPlay={true}
        muted="muted"
        playsInline={true}
        style={{
          ...VideoTrackPlayer.defaultProps.CameraStyle,
          ...props.CameraStyle,
        }}
      ></video>

      <Modal
        title="Camera Configuration"
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        onOk={() => form.submit()}
        okText="Apply"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleConfigSubmit}
          initialValues={{
            resolution: getCurrentResolutionString(),
            fps: getCurrentFPSValue(),
          }}
        >
          <Form.Item
            label="Resolution"
            name="resolution"
            rules={[{ required: true, message: "Please select a resolution!" }]}
          >
            <Select
              options={getResolutionOptions()}
              placeholder="Select resolution"
            />
          </Form.Item>
          <Form.Item
            label="Frame Rate (FPS)"
            name="fps"
            rules={[{ required: true, message: "Please select frame rate!" }]}
          >
            <Select options={getFPSOptions()} placeholder="Select frame rate" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

VideoTrackPlayer.defaultProps = {
  CameraStyle: {
    // borderStyle: "dashed",
    width: "100%",
    height: "100%",
    // maxHeight: "90vh",
    // maxWidth: "1800px"
  },
};

export default VideoTrackPlayer;
